// Multiplayer client: one WebSocket to the shared-world server, JSON protocol
// (see server/mp-server.mjs). Reconnects with backoff; everything the rest of
// the game needs arrives through the callback bag passed to the constructor.
const TICK_MS = 100; // 10 Hz state sends

function wsUrl() {
  // gated :8110 serves at /, proofplay.dk serves under /ashvale/ — the ws
  // endpoint sits next to wherever the page lives
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const base = location.pathname.replace(/[^/]*$/, ''); // strip index.html
  return `${proto}//${location.host}${base}ws`.replace(/([^:])\/\//g, '$1/');
}

export class NetClient {
  // cb: { onWelcome, onJoin, onLeave, onRename, onStates, onEvent, onStatus }
  constructor(cb) {
    this.cb = cb;
    this.id = localStorage.getItem('ashvale-pid')
      || crypto.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('ashvale-pid', this.id);
    this.name = localStorage.getItem('ashvale-name') || 'Survivor';
    this.ws = null;
    this.connected = false;
    this.online = new Map(); // id -> name (kept current from join/leave)
    this.stateFn = null;     // set by main: () => state object
    this._backoff = 1000;
    this._sendTimer = null;
    this._closed = false;
    this.connect();
  }

  connect() {
    if (this._closed) return;
    this.cb.onStatus?.('connecting');
    let ws;
    try { ws = new WebSocket(wsUrl()); } catch { return this._retry(); }
    this.ws = ws;
    ws.onopen = () => {
      this._backoff = 1000;
      this.send({ t: 'hello', id: this.id, name: this.name });
    };
    ws.onmessage = (e) => {
      let m;
      try { m = JSON.parse(e.data); } catch { return; }
      if (m.t === 'welcome') {
        this.connected = true;
        this.online = new Map(m.players.map((p) => [p.id, p.name]));
        this.cb.onStatus?.('online');
        this.cb.onWelcome?.(m);
        clearInterval(this._sendTimer);
        this._sendTimer = setInterval(() => {
          const s = this.stateFn?.();
          if (s) this.send({ t: 's', s });
        }, TICK_MS);
      } else if (m.t === 'join') {
        this.online.set(m.id, m.name);
        this.cb.onJoin?.(m);
      } else if (m.t === 'leave') {
        this.online.delete(m.id);
        this.cb.onLeave?.(m);
      } else if (m.t === 'rename') {
        this.online.set(m.id, m.name);
        this.cb.onRename?.(m);
      } else if (m.t === 'states') {
        this.cb.onStates?.(m.s);
      } else if (m.t === 'wolves') {
        this.cb.onWolves?.(m.w);
      } else if (m.t === 'inf') {
        // the server owns the horde (server/zombies.mjs): 5 Hz snapshot rows,
        // already filtered to this client's 220 m (Z8, Z79)
        this.cb.onInfected?.(m.z);
      } else if (m.t === 'ev') {
        this.cb.onEvent?.(m.ev, m.from);
      }
    };
    ws.onclose = () => {
      const was = this.connected;
      this.connected = false;
      clearInterval(this._sendTimer);
      if (was) this.cb.onStatus?.('offline');
      this._retry();
    };
    ws.onerror = () => { try { ws.close(); } catch { /* already closed */ } };
  }

  _retry() {
    if (this._closed) return;
    this.cb.onStatus?.('offline');
    setTimeout(() => this.connect(), this._backoff);
    this._backoff = Math.min(15000, this._backoff * 1.7);
  }

  send(msg) { if (this.ws?.readyState === 1) this.ws.send(JSON.stringify(msg)); }
  event(ev) { this.send({ t: 'ev', ev }); }
  save(snapshot) { this.send({ t: 'save', ...snapshot }); }

  setName(name) {
    this.name = (name || 'Survivor').slice(0, 24).trim() || 'Survivor';
    localStorage.setItem('ashvale-name', this.name);
    this.send({ t: 'name', name: this.name });
  }
}
